import { useNavigate, useLocation } from "react-router-dom";

// Pass this as `backUrl` (as a prop, or in navigate() state) to make the
// Back button pop the real history entry (navigate(-1)) instead of pushing
// a fresh one. Only use it for a screen guaranteed to have been reached by
// an in-app forward navigate() call, so a "-1" entry is guaranteed to exist
// — e.g. a document viewer opened from a list, where the list's own history
// entry already holds the correct state and pushing a reconstructed copy on
// Back just grows the stack forever (each Back tap added an entry instead
// of removing one, so repeated taps cycled through the same screens instead
// of returning to where the user came from). Every other `backUrl` value
// keeps the original push behavior unchanged.
export const NAVIGATE_BACK_IN_HISTORY = "__navigate_back_in_history__";

interface UseBackNavigationProps {
  onBack?: () => void;
  backUrl?: string;
  // Optional state to re-attach when navigating to backUrl — a plain
  // navigate(path) (unlike navigate(-1)) starts a fresh history entry with
  // no state, which silently empties any destination page that renders
  // purely from location.state (e.g. a list passed in via navigation state
  // with no API refetch). Pass the original state back through here so it
  // survives the round trip.
  backState?: unknown;
}

export const useBackNavigation = ({
  onBack,
  backUrl,
  backState,
}: UseBackNavigationProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Try props, then location.state, then query param
  let resolvedBackUrl = backUrl;
  if (!resolvedBackUrl && location.state && (location.state as any).backUrl) {
    resolvedBackUrl = (location.state as any).backUrl;
  }
  if (!resolvedBackUrl) {
    const params = new URLSearchParams(location.search);
    const urlParam = params.get("backUrl");
    if (urlParam) resolvedBackUrl = urlParam;
  }

  let resolvedBackState = backState;
  if (
    resolvedBackState === undefined &&
    location.state &&
    (location.state as any).backState !== undefined
  ) {
    resolvedBackState = (location.state as any).backState;
  }

  const handleBack = () => {
    if (onBack) return onBack();
    if (resolvedBackUrl === NAVIGATE_BACK_IN_HISTORY) {
      return navigate(-1);
    }
    if (resolvedBackUrl) {
      return navigate(
        resolvedBackUrl,
        resolvedBackState !== undefined
          ? { state: resolvedBackState }
          : undefined
      );
    }
    // Fallback: parent path
    const currentPath = window.location.pathname.replace(/\/$/, "");
    const pathSegments = currentPath.split("/").filter(Boolean);
    pathSegments.pop(); // Remove the last segment

    // Rebuild the path and navigate
    const newPath = `/${pathSegments.join("/")}`;
    return navigate(newPath);
  };

  return {
    handleBack,
  };
};
